import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      <img
        alt="Lamborghini amarillo Taller Kevin Alberto Ronda"
        className="absolute inset-0 w-full h-full object-cover object-center scale-105 transition-transform duration-[8000ms] ease-out"
        style={{ transform: visible ? 'scale(1)' : 'scale(1.05)' }}
        fetchPriority="high"
        decoding="async"
        src="https://public.readdy.ai/ai/img_res/edited_cf5f49ab1140a20ec7a1c97d7d794c41_30d692f2.jpg"
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/35 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent pointer-events-none" />

      {/* Top/bottom bars */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black to-transparent z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent z-10" />

      {/* Decorative vertical line */}
      <div className="absolute left-10 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-yellow-400/20 to-transparent hidden lg:block" />

      <div className="relative z-20 h-full flex flex-col items-center justify-center px-6 w-full">
        <div className="text-center w-full max-w-4xl mx-auto">

          {/* Tag line */}
          <div
            className={`inline-flex items-center gap-3 mb-7 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.1s' }}
          >
            <span className="w-8 h-px bg-yellow-400" />
            <span className="text-yellow-400 text-[10px] font-bold uppercase tracking-[0.35em]">
              Ronda, Málaga · Taller Oficial
            </span>
            <span className="w-8 h-px bg-yellow-400" />
          </div>

          {/* Main headline */}
          <div
            className={`mb-3 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '0.25s' }}
          >
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl uppercase leading-none text-white tracking-wider">
              Taller <span className="text-yellow-400">Kevin</span>
            </h1>
          </div>

          <div
            className={`mb-7 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase leading-none text-white/60 tracking-widest">
              Alberto
            </h2>
          </div>

          {/* Divider */}
          <div
            className={`flex justify-center mb-7 transition-all duration-700 ${
              visible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '0.55s' }}
          >
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
          </div>

          {/* Subtitle */}
          <p
            className={`text-zinc-300 text-xs md:text-sm font-light uppercase tracking-[0.3em] mb-10 px-2 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.65s' }}
          >
            Mecánica · Diagnosis · Electrónica · Láminas
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.8s' }}
          >
            <Link
              to="/contacto"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-yellow-400 text-black px-9 py-4 text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-yellow-300 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Pide tu presupuesto gratis
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/servicios"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 border border-white/25 text-white px-9 py-4 text-[11px] font-bold uppercase tracking-[0.2em] hover:border-yellow-400/60 hover:text-yellow-400 hover:bg-yellow-400/5 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Ver servicios
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-20 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 z-20 transition-all duration-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1.1s' }}
      >
        <span className="text-white/25 text-[9px] uppercase tracking-[0.4em]">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-yellow-400/50 to-transparent animate-float" />
      </div>

      {/* Stats strip at bottom */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 hidden md:block transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: '1s' }}
      >
        <div className="flex divide-x divide-zinc-800 border-t border-zinc-800/50">
          {[
            { icon: 'ri-award-line', value: '25+', label: 'Años de experiencia' },
            { icon: 'ri-user-heart-line', value: '5.000+', label: 'Clientes satisfechos' },
            { icon: 'ri-phone-line', value: '+34 666 36 59 22', label: 'Llámanos ahora' },
          ].map((s) => (
            <div
              key={s.label}
              className="flex-1 flex items-center gap-3 px-6 py-3 bg-black/70 backdrop-blur-sm"
            >
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className={`${s.icon} text-yellow-400 text-sm`} />
              </div>
              <div>
                <p className="text-white text-sm font-bold leading-none">{s.value}</p>
                <p className="text-zinc-500 text-[10px] mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
